import { z } from "zod";
import { requestedCacheWrites, THIRTY_MINUTES, TWELVE_HOURS } from "../../shared/cache-writes";
import type { ModelErrorType } from "../../shared/errors";
import {
  argumentsText,
  contentText,
  errorCode,
  finiteNumber,
  isRecord,
  jsonObject,
  jsonObjects,
  lastMatching,
  nonEmptyString,
  outputType,
  parseArguments,
  readableText,
  reasoningText,
  records,
} from "../../shared/json-body";
import { canonicalModelName } from "../../shared/models";
import { type RoutingFacts, skuById, skuIdOf } from "../../shared/pricing";
import { encryptedReasoningPart, toolPart } from "../../shared/prompt";
import type {
  DeclaredTool,
  ModelPrompt,
  ModelReply,
  ModelRequestSettings,
  ModelToolCall,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  StreamChunk,
} from "../../shared/transport";
import type { CacheWrite, ModelUsage } from "../../shared/usage";

/** The most output a request may ask for when OpenAI's price table gives no limit. */
const MAX_OUTPUT_TOKENS = 128_000;

/** How long a cache write lasts. Writes for twelve hours cost twice what thirty-minute ones do. */
const PromptCacheTtl = z.enum(["30m", "12h"]);

/** Marks the end of a prefix to cache, as a content block's last word. */
const PromptCacheBreakpoint = z.strictObject({
  mode: z.literal("explicit").optional(),
  ttl: PromptCacheTtl.optional(),
});

const InputContent = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("input_text"),
    text: z.string(),
    prompt_cache_breakpoint: PromptCacheBreakpoint.optional(),
  }),
  z.strictObject({
    type: z.literal("input_image"),
    image_url: z.string(),
    detail: z.enum(["low", "high", "auto", "original"]).optional(),
    prompt_cache_breakpoint: PromptCacheBreakpoint.optional(),
  }),
  z.strictObject({
    type: z.literal("input_file"),
    prompt_cache_breakpoint: PromptCacheBreakpoint.optional(),
    file_data: z.string().optional(),
    file_url: z.url().optional(),
    filename: z.string().optional(),
  }),
]);

const OutputContent = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("output_text"),
    text: z.string(),
    annotations: z.array(z.looseObject({ type: z.string() })).optional(),
    logprobs: z.array(z.looseObject({})).optional(),
  }),
  z.strictObject({ type: z.literal("refusal"), refusal: z.string() }),
]);

/** A message the caller writes, or one the model sent in an earlier response. */
const Message = z.strictObject({
  type: z.literal("message").optional(),
  id: z.string().optional(),
  role: z.enum(["user", "assistant", "system", "developer"]),
  content: z.union([z.string(), z.array(z.union([InputContent, OutputContent]))]),
  status: z.enum(["in_progress", "completed", "incomplete"]).optional(),
  phase: z.enum(["commentary", "final_answer"]).nullable().optional(),
});

const itemStatus = z.enum(["in_progress", "completed", "incomplete"]).optional();
const toolOutput = z.union([z.string(), z.array(InputContent)]);

/** Items the model sent in an earlier response, carried back so the model sees its own turn. */
const Item = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("function_call"),
    id: z.string().optional(),
    call_id: z.string(),
    name: z.string(),
    namespace: z.string().optional(),
    arguments: z.string(),
    status: itemStatus,
  }),
  z.strictObject({
    type: z.literal("function_call_output"),
    id: z.string().nullable().optional(),
    call_id: z.string(),
    output: toolOutput,
    status: itemStatus.nullable(),
  }),
  z.strictObject({
    type: z.literal("custom_tool_call"),
    id: z.string().optional(),
    call_id: z.string(),
    name: z.string(),
    namespace: z.string().optional(),
    input: z.string(),
  }),
  z.strictObject({
    type: z.literal("custom_tool_call_output"),
    id: z.string().optional(),
    call_id: z.string(),
    output: toolOutput,
  }),
  z.strictObject({
    type: z.literal("reasoning"),
    id: z.string().optional(),
    summary: z.array(z.strictObject({ type: z.literal("summary_text"), text: z.string() })),
    content: z
      .array(z.strictObject({ type: z.literal("reasoning_text"), text: z.string() }))
      .optional(),
    encrypted_content: z.string().nullable().optional(),
    status: itemStatus,
  }),
  z.strictObject({
    type: z.literal("compaction"),
    id: z.string().optional(),
    encrypted_content: z.string(),
  }),
  z.strictObject({
    type: z.literal("local_shell_call"),
    id: z.string().optional(),
    call_id: z.string(),
    action: z.looseObject({ type: z.literal("exec") }),
    status: itemStatus,
  }),
  z.strictObject({
    type: z.literal("local_shell_call_output"),
    id: z.string().optional(),
    call_id: z.string().optional(),
    output: z.string(),
    status: itemStatus.nullable(),
  }),
  z.strictObject({
    type: z.literal("shell_call"),
    id: z.string().optional(),
    call_id: z.string(),
    action: z.looseObject({}),
    status: itemStatus,
  }),
  z.strictObject({
    type: z.literal("shell_call_output"),
    id: z.string().optional(),
    call_id: z.string(),
    output: z.array(z.looseObject({})),
    max_output_length: z.number().int().nullable().optional(),
  }),
  z.strictObject({
    type: z.literal("apply_patch_call"),
    id: z.string().optional(),
    call_id: z.string(),
    operation: z.looseObject({ type: z.string() }),
    status: z.enum(["in_progress", "completed"]),
  }),
  z.strictObject({
    type: z.literal("apply_patch_call_output"),
    id: z.string().optional(),
    call_id: z.string(),
    output: z.string().nullable().optional(),
    status: z.enum(["completed", "failed"]),
  }),
  z.strictObject({
    type: z.literal("web_search_call"),
    id: z.string(),
    status: z.string(),
    action: z.looseObject({ type: z.string() }).optional(),
  }),
]);

const InputItem = z.union([Message, Item]);

const FunctionTool = z.strictObject({
  type: z.literal("function"),
  name: z.string(),
  description: z.string().nullable().optional(),
  parameters: z.record(z.string(), z.unknown()).nullable(),
  strict: z.boolean().nullable(),
  defer_loading: z.boolean().optional(),
});

const CustomTool = z.strictObject({
  type: z.literal("custom"),
  name: z.string(),
  description: z.string().optional(),
  format: z
    .discriminatedUnion("type", [
      z.strictObject({ type: z.literal("text") }),
      z.strictObject({
        type: z.literal("grammar"),
        syntax: z.enum(["lark", "regex"]),
        definition: z.string(),
      }),
    ])
    .optional(),
  defer_loading: z.boolean().optional(),
});

const webSearchTool = <Type extends string>(type: Type) =>
  z.strictObject({
    type: z.literal(type),
    filters: z
      .strictObject({ allowed_domains: z.array(z.string()).nullable().optional() })
      .nullable()
      .optional(),
    search_context_size: z.enum(["low", "medium", "high"]).optional(),
    user_location: z
      .strictObject({
        type: z.literal("approximate").optional(),
        city: z.string().nullable().optional(),
        country: z.string().nullable().optional(),
        region: z.string().nullable().optional(),
        timezone: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    external_web_access: z.boolean().optional(),
  });

/**
 * Tools the caller runs, plus web search, which OpenAI runs and Pax prices. File search, code
 * interpreter, computer use, image generation, MCP and hosted shells are refused: they keep state
 * at OpenAI or have no price.
 */
const Tool = z.discriminatedUnion("type", [
  FunctionTool,
  CustomTool,
  z.strictObject({
    type: z.literal("namespace"),
    name: z.string(),
    description: z.string(),
    tools: z.array(z.discriminatedUnion("type", [FunctionTool, CustomTool])),
  }),
  z.strictObject({ type: z.literal("local_shell") }),
  z.strictObject({
    type: z.literal("shell"),
    environment: z
      .strictObject({ type: z.literal("local") })
      .nullable()
      .optional(),
  }),
  z.strictObject({ type: z.literal("apply_patch") }),
  webSearchTool("web_search"),
  webSearchTool("web_search_2025_08_26"),
  webSearchTool("web_search_preview"),
  webSearchTool("web_search_preview_2025_03_11"),
]);

const ToolChoice = z.union([
  z.enum(["none", "auto", "required"]),
  z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("function"), name: z.string() }),
    z.strictObject({ type: z.literal("custom"), name: z.string() }),
    z.strictObject({
      type: z.literal("allowed_tools"),
      mode: z.enum(["auto", "required"]),
      tools: z.array(z.record(z.string(), z.unknown())),
    }),
    z.strictObject({ type: z.literal("apply_patch") }),
    z.strictObject({ type: z.literal("shell") }),
    z.strictObject({ type: z.literal("web_search_preview") }),
  ]),
]);

export const OpenAiResponsesRequest = z
  .strictObject({
    model: z.string().min(1).meta({ example: "gpt-6-sol" }),
    max_output_tokens: z.number().int().positive().meta({
      description: "Required. At most the model's own limit.",
      example: 8192,
    }),
    input: z.union([z.string(), z.array(InputItem)]),
    instructions: z.string().nullable().optional(),
    include: z
      .array(
        z.enum([
          "reasoning.encrypted_content",
          "web_search_call.action.sources",
          "web_search_call.results",
          "message.output_text.logprobs",
        ]),
      )
      .nullable()
      .optional(),
    max_tool_calls: z.number().int().positive().nullable().optional(),
    metadata: z.record(z.string(), z.string()).nullable().optional(),
    parallel_tool_calls: z.boolean().nullable().optional(),
    prompt_cache_key: z.string().optional(),
    prompt_cache_options: z
      .strictObject({
        mode: z.enum(["implicit", "explicit"]).optional(),
        ttl: PromptCacheTtl.optional(),
        prewarm: z.boolean().optional(),
      })
      .optional(),
    reasoning: z
      .strictObject({
        effort: z.enum(["none", "minimal", "low", "medium", "high", "xhigh"]).nullable().optional(),
        summary: z.enum(["auto", "concise", "detailed"]).nullable().optional(),
      })
      .nullable()
      .optional(),
    safety_identifier: z.string().optional(),
    service_tier: z.enum(["auto", "default", "flex", "priority", "fast"]).nullable().optional(),
    store: z.literal(false).default(false).meta({
      description: "Always false: Pax sends every request with `store: false`.",
    }),
    stream: z.boolean().nullable().optional(),
    stream_options: z
      .strictObject({ include_obfuscation: z.boolean().optional() })
      .nullable()
      .optional(),
    temperature: z.number().min(0).max(2).nullable().optional(),
    text: z
      .strictObject({
        format: z
          .discriminatedUnion("type", [
            z.strictObject({ type: z.literal("text") }),
            z.strictObject({ type: z.literal("json_object") }),
            z.strictObject({
              type: z.literal("json_schema"),
              name: z.string(),
              schema: z.record(z.string(), z.unknown()),
              description: z.string().optional(),
              strict: z.boolean().nullable().optional(),
            }),
          ])
          .optional(),
        verbosity: z.enum(["low", "medium", "high"]).nullable().optional(),
      })
      .optional(),
    tool_choice: ToolChoice.optional(),
    tools: z.array(Tool).optional(),
    top_logprobs: z.number().int().min(0).max(20).nullable().optional(),
    top_p: z.number().min(0).max(1).nullable().optional(),
    truncation: z.enum(["auto", "disabled"]).nullable().optional(),
    user: z.string().optional(),
  })
  .meta({
    id: "OpenAiResponsesRequest",
    description:
      "An OpenAI Responses request. Every field OpenAI documents is accepted except those that keep state at OpenAI, such as `previous_response_id`, `conversation`, `background` and `prompt`, and `store` other than false. Any other field is refused.",
    example: { model: "gpt-6-sol", max_output_tokens: 8192, input: "Who holds Burgundy in 1444?" },
  });
export type OpenAiResponsesRequest = z.infer<typeof OpenAiResponsesRequest>;

export const OpenAiResponse = z
  .looseObject({
    id: z.string(),
    object: z.literal("response"),
    model: z.string(),
    usage: z.looseObject({}).nullable().optional(),
  })
  .meta({
    id: "OpenAiResponse",
    description:
      "OpenAI's response, byte for byte: a response object, or its server-sent events when `stream` is true.",
  });

/** OpenAI's Responses API, called directly and priced from OpenAI's published rates. */
export const openaiResponses: ModelTransportDefinition<OpenAiResponsesRequest> = {
  vendor: "openai",
  provider: "openai",
  acceptsPath: path => path === "v1/responses",
  upstreamUrl: () => "https://api.openai.com/v1/responses",
  credential: "OPENAI_API_KEY",
  authorize: (headers, credential) => headers.set("authorization", `Bearer ${credential}`),
  request: OpenAiResponsesRequest,
  headers: {
    "content-type": z.string().regex(/^application\/json\b/),
    accept: z.string(),
  },
  query: {},
  billedBy: "openai",
  readRequestedFacts: request => {
    const body = jsonObject(request.body);
    return openaiRequestedFacts(nonEmptyString(body.model), nonEmptyString(body.service_tier));
  },
  skuOf: openaiSkuOf,
  refusal: (_path, request) => {
    const sku = skuById(
      openaiSkuOf(openaiRequestedFacts(request.model, request.service_tier ?? undefined)),
    );
    if (!sku)
      return { refusal: "model_not_found", message: `Pax has no price for ${request.model}.` };
    const limit = sku.maxOutputTokens ?? MAX_OUTPUT_TOKENS;
    if (request.max_output_tokens > limit)
      return {
        refusal: "invalid_request",
        message: `max_output_tokens is ${request.max_output_tokens}, above ${request.model}'s limit of ${limit}.`,
      };
    return null;
  },
  readModel: (_path, requestBody) => nonEmptyString(jsonObject(requestBody).model),
  readUsage,
  readSettings: (_path, requestBody) => readSettings(requestBody),
  readPrompt,
  readReply,
  readChunks,
  readOutputContract: (_path, requestBody) => readOutputContract(requestBody),
  errorTypeOf: code => (code ? openaiErrorTypes[code] : undefined),
};

const openaiErrorTypes: Record<string, ModelErrorType> = {
  context_length_exceeded: "context_length_exceeded",
  rate_limit_exceeded: "rate_limited",
  insufficient_quota: "billing",
  invalid_api_key: "auth",
  model_not_found: "model_not_found",
  server_error: "server_error",
  invalid_request_error: "invalid_request",
  invalid_prompt: "invalid_request",
};

/**
 * Function tools take JSON arguments and custom tools free text. Hosted tools such as web search
 * name their calls after the call's type, not the tool, so their calls can't be checked by name.
 */
function readOutputContract(requestBody: string): OutputContract {
  const body = jsonObject(requestBody);
  const textOptions = isRecord(body.text) ? body.text : {};
  const format = isRecord(textOptions.format) ? textOptions.format : {};
  const tools = new Map<string, DeclaredTool>();
  let openTools = false;
  const declare = (definition: Record<string, unknown>) => {
    const name = nonEmptyString(definition.name);
    if (definition.type === "namespace")
      for (const tool of records(definition.tools)) declare(tool);
    else if (definition.type === "function" && name)
      tools.set(name, { json: true, schema: definition.parameters });
    else if (definition.type === "custom" && name) tools.set(name, { json: false });
    else openTools = true;
  };
  for (const tool of records(body.tools)) declare(tool);
  return {
    tools,
    openTools,
    json: format.type === "json_schema" || format.type === "json_object",
    responseSchema: format.type === "json_schema" ? format.schema : undefined,
  };
}

/** A stream names each output item as it opens, then sends its deltas by item id. */
function readChunks(responseBody: string): StreamChunk[] {
  const toolNames = new Map<string, string>();
  return jsonObjects(responseBody).map((event): StreamChunk => {
    const type = String(event.type ?? "");
    const item = isRecord(event.item) ? event.item : {};
    const added = type.endsWith(".delta") && typeof event.delta === "string" ? event.delta : "";
    if (type === "response.output_item.added" && item.type === "function_call") {
      const call = nonEmptyString(item.id) ?? "";
      const name = nonEmptyString(item.name) ?? "a tool";
      toolNames.set(call, name);
      return { kind: "tool", tool: name, call, text: "" };
    }
    if (type.startsWith("response.function_call_arguments.")) {
      const call = nonEmptyString(event.item_id) ?? "";
      return { kind: "tool", tool: toolNames.get(call) ?? "a tool", call, text: added };
    }
    if (type.startsWith("response.reasoning")) return { kind: "reasoning", text: added };
    if (type.startsWith("response.output_text.")) return { kind: "text", text: added };
    if (endingEvents.includes(type)) return { kind: "usage" };
    if (!type) return isRecord(event.usage) ? { kind: "usage" } : { kind: "other" };
    return { kind: "other" };
  });
}

/** The events a stream ends with, each carrying the whole response and its usage. */
const endingEvents = ["response.completed", "response.incomplete", "response.failed"];

/**
 * Responses report usage as `input_tokens` and `output_tokens`, either on the document itself or
 * on the response a stream's ending event carries. Each `web_search_call` output item is one use
 * of `web_search`.
 */
function readUsage(body: string, requestBody: string): ModelUsage | undefined {
  for (const event of jsonObjects(body).reverse()) {
    const response =
      typeof event.type === "string" &&
      endingEvents.includes(event.type) &&
      isRecord(event.response)
        ? event.response
        : event;
    if (!isRecord(response.usage)) continue;
    const usage = response.usage;
    const inputTokens = finiteNumber(usage.input_tokens);
    const outputTokens = finiteNumber(usage.output_tokens);
    if (inputTokens === undefined && outputTokens === undefined) continue;
    const input = isRecord(usage.input_tokens_details) ? usage.input_tokens_details : {};
    const output = isRecord(usage.output_tokens_details) ? usage.output_tokens_details : {};
    const written = finiteNumber(input.cache_write_tokens) ?? 0;
    const webSearches = records(response.output).filter(
      item => item.type === "web_search_call",
    ).length;
    return {
      model: nonEmptyString(response.model),
      inputTokens: inputTokens ?? 0,
      audioInputTokens: finiteNumber(input.audio_tokens) ?? 0,
      implicitCacheReadTokens: finiteNumber(input.cached_tokens) ?? 0,
      explicitCacheReadTokens: 0,
      cacheWrites: cacheWritesOf(input, written, requestBody),
      outputTokens: outputTokens ?? 0,
      reasoningTokens: finiteNumber(output.reasoning_tokens) ?? 0,
      toolUses: webSearches ? { web_search: webSearches } : {},
      serviceTier: nonEmptyString(response.service_tier),
    };
  }
}

/**
 * Cache writes by lifetime: the thirty-minute and twelve-hour counts the response reports, or else
 * the lifetime the request asked for, thirty minutes when it asked for none.
 */
function cacheWritesOf(
  input: Record<string, unknown>,
  written: number,
  requestBody: string,
): CacheWrite[] {
  const byLifetime = isRecord(input.cache_write_tokens_details)
    ? input.cache_write_tokens_details
    : undefined;
  if (byLifetime)
    return [
      { tokens: finiteNumber(byLifetime.ttl_30m_tokens) ?? 0, ttlSeconds: THIRTY_MINUTES },
      { tokens: finiteNumber(byLifetime.ttl_12h_tokens) ?? 0, ttlSeconds: TWELVE_HOURS },
    ].filter(write => write.tokens > 0);
  return requestedCacheWrites(written, requestBody).map(write =>
    write.ttlSeconds === undefined && !write.possibleTtlSeconds
      ? { ...write, ttlSeconds: THIRTY_MINUTES }
      : write,
  );
}

function readSettings(requestBody: string): ModelRequestSettings {
  const body = jsonObject(requestBody);
  const reasoning = isRecord(body.reasoning) ? body.reasoning : {};
  const textOptions = isRecord(body.text) ? body.text : {};
  const format = isRecord(textOptions.format) ? textOptions.format : {};
  return {
    model: nonEmptyString(body.model),
    maxTokens: finiteNumber(body.max_output_tokens),
    temperature: finiteNumber(body.temperature),
    topP: finiteNumber(body.top_p),
    stream: body.stream === true,
    reasoningLevel: nonEmptyString(reasoning.effort),
    outputType: outputType(nonEmptyString(format.type)),
    promptCacheKey: nonEmptyString(body.prompt_cache_key),
  };
}

/**
 * The instructions and the system messages the input opens with, the request's tools, then every
 * other input item in order. A system message sent partway through the input stays where it was
 * sent.
 */
function readPrompt(requestBody: string): ModelPrompt {
  const body = jsonObject(requestBody);
  const system: PromptPart[] = [];
  const tools = records(body.tools).flatMap(toolParts);
  const conversation: PromptPart[] = [];
  const toolResults = new Map<string, string>();
  const instructions = nonEmptyString(body.instructions);
  if (instructions)
    system.push({ role: "system", label: "System", text: instructions, sent: instructions });
  const input =
    typeof body.input === "string" ? [{ role: "user", content: body.input }] : body.input;
  for (const item of records(input)) {
    const type = nonEmptyString(item.type) ?? "message";
    const opening = conversation.length === 0;
    if (type === "message") {
      const role = nonEmptyString(item.role);
      const content = contentText(item.content);
      if (role === "system" || role === "developer")
        (opening ? system : conversation).push({
          role: "system",
          label: "System",
          text: content,
          sent: content,
        });
      else if (role === "assistant")
        conversation.push({ role: "assistant", label: "Assistant", text: content, sent: content });
      else conversation.push({ role: "user", label: "User", text: content, sent: content });
    } else if (isToolResult(type)) {
      const output = toolResultText(item.output);
      const id = nonEmptyString(item.call_id);
      if (id) toolResults.set(id, output);
      conversation.push({
        role: "tool",
        label: "Tool result",
        text: readableText(output),
        sent: argumentsText(item.output),
      });
    } else if (isToolCall(type)) {
      const call = toolCallOf(item, type);
      conversation.push({
        role: "assistant",
        label: `Calls ${call.name}`,
        text: readableText(call.arguments),
        sent: argumentsText(item.arguments ?? item.input ?? item.action),
      });
    } else if (type === "reasoning") {
      const summary = reasoningText(item);
      conversation.push(
        summary || !item.encrypted_content
          ? { role: "assistant", label: "Reasoning", text: summary, sent: summary }
          : encryptedReasoningPart(argumentsText(item.encrypted_content)),
      );
    } else {
      conversation.push({
        role: "user",
        label: type,
        text: JSON.stringify(item, null, 2),
        sent: JSON.stringify(item),
      });
    }
  }
  return { parts: [...system, ...tools, ...conversation], toolResults };
}

/**
 * Items that call a tool: `function_call`, Codex's `custom_tool_call` and `local_shell_call`, and
 * hosted tools such as `web_search_call`. Each one's result is the same type with `_output`.
 */
const isToolCall = (type: string) => type.endsWith("_call");
const isToolResult = (type: string) => type.endsWith("_call_output");

function toolCallOf(item: Record<string, unknown>, type: string): ModelToolCall {
  return {
    id: nonEmptyString(item.call_id) ?? nonEmptyString(item.id),
    name: nonEmptyString(item.name) ?? type.slice(0, -"_call".length),
    arguments: parseArguments(item.arguments ?? item.input ?? item.action),
  };
}

/** A tool's result as text: a string as is, text parts joined, anything else as JSON. */
function toolResultText(output: unknown): string {
  if (typeof output === "string" || Array.isArray(output)) return contentText(output);
  return output === undefined ? "" : JSON.stringify(output, null, 2);
}

/** A tool definition as prompt parts; a namespace of tools becomes one part per tool in it. */
function toolParts(definition: Record<string, unknown>): PromptPart[] {
  return definition.type === "namespace"
    ? records(definition.tools).map(toolPart)
    : [toolPart(definition)];
}

/** Reads the final response document, whether it came whole or as the last streamed event. */
function readReply(responseBody: string): ModelReply {
  const events = jsonObjects(responseBody);
  const finalEvent = lastMatching(events, event => endingEvents.includes(String(event.type)));
  const document = isRecord(finalEvent?.response)
    ? finalEvent.response
    : lastMatching(events, event => event.object === "response");
  const reply: ModelReply = {
    reasoning: "",
    reasoningEncryptedWithoutSummary: false,
    text: "",
    toolCalls: [],
    finishReasons: [],
    nativeFinishReasons: [],
  };
  if (!document) {
    const failure = lastMatching(events, event => isRecord(event.error));
    return {
      ...reply,
      errorType: isRecord(failure?.error) ? errorCode(failure.error) : undefined,
      errorMessage: isRecord(failure?.error) ? nonEmptyString(failure.error.message) : undefined,
    };
  }
  const outputItems = records(document.output);
  for (const item of outputItems) {
    const type = nonEmptyString(item.type) ?? "";
    if (type === "message") reply.text += contentText(item.content);
    else if (type === "reasoning") {
      const summary = reasoningText(item);
      if (summary) reply.reasoning += (reply.reasoning ? "\n\n" : "") + summary;
      else if (item.encrypted_content) reply.reasoningEncryptedWithoutSummary = true;
    } else if (isToolCall(type)) reply.toolCalls.push(toolCallOf(item, type));
  }
  if (reply.reasoning) reply.reasoningEncryptedWithoutSummary = false;
  return {
    ...reply,
    id: nonEmptyString(document.id),
    model: nonEmptyString(document.model),
    finishReasons:
      outputItems.length || document.status ? [finishReason(document, reply.toolCalls)] : [],
    errorType: isRecord(document.error) ? errorCode(document.error) : undefined,
    errorMessage: isRecord(document.error) ? nonEmptyString(document.error.message) : undefined,
    serviceTier: nonEmptyString(document.service_tier),
  };
}

function finishReason(document: Record<string, unknown>, toolCalls: readonly ModelToolCall[]) {
  if (document.status === "failed") return "error";
  if (document.status === "incomplete") {
    const details = isRecord(document.incomplete_details) ? document.incomplete_details : {};
    return details.reason === "content_filter" ? "content_filter" : "length";
  }
  return toolCalls.length ? "tool_call" : "stop";
}

/**
 * What an OpenAI request asked for. `auto` runs on the default tier for a project without Scale
 * Tier, which Pax has none of, so it is asked for as default.
 */
function openaiRequestedFacts(
  model: string | undefined,
  serviceTier: string | undefined,
): Partial<RoutingFacts> {
  return {
    ...(model === undefined ? {} : { model }),
    tier: serviceTier === undefined || serviceTier === "auto" ? "default" : serviceTier,
  };
}

function openaiSkuOf(requested: Partial<RoutingFacts>): string | undefined {
  const { model, tier } = requested;
  if (model === undefined || tier === undefined) return;
  return skuIdOf({
    billedBy: "openai",
    model: canonicalModelName(model),
    endpoint: "global",
    tier,
  });
}
